<template>
  <div class="equipment-table">
    <!-- 后端连接提示 -->
    <el-alert
      v-if="backendError"
      title="后端服务未连接"
      type="warning"
      :closable="false"
      description="请启动 Django 服务：python manage.py runserver 0.0.0.0:8000"
      show-icon
      style="margin-bottom: 15px"
    />

    <!-- 查询条件：留空则查询全部；折叠后只显示标题栏与按钮 -->
    <el-card class="search-card" shadow="never">
      <div slot="header" class="search-header">
        <span class="search-title" @click="searchExpanded = !searchExpanded">
          查询条件
          <i :class="searchExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
        </span>
      </div>
      <el-collapse-transition>
        <el-form v-show="searchExpanded" :model="queryForm" inline class="search-form">
          <el-form-item label="使用科室">
            <department-select v-model="queryForm.department" placeholder="全部科室" clearable style="width: 160px" />
          </el-form-item>
          <el-form-item label="设备名称">
            <el-input v-model="queryForm.name" placeholder="设备名称" clearable />
          </el-form-item>
          <el-form-item label="出厂编号">
            <el-input v-model="queryForm.factory_no" placeholder="出厂编号" clearable />
          </el-form-item>
          <el-form-item label="生产厂家">
            <el-input v-model="queryForm.manufacturer" placeholder="生产厂家" clearable />
          </el-form-item>
          <el-form-item label="设备型号">
            <el-input v-model="queryForm.model" placeholder="设备型号" clearable />
          </el-form-item>
          <el-form-item label="使用状态">
            <el-select v-model="queryForm.status" placeholder="全部" clearable>
              <el-option label="在用" :value="1" />
              <el-option label="报废" :value="2" />
              <el-option label="维修" :value="3" />
              <el-option label="停用" :value="4" />
            </el-select>
          </el-form-item>
          <el-form-item label="固定资产">
            <el-select v-model="queryForm.is_fixed_asset" placeholder="全部" clearable>
              <el-option label="是" :value="true" />
              <el-option label="否" :value="false" />
            </el-select>
          </el-form-item>
          <!-- 回收站：与"使用状态/固定资产"同款下拉，三列布局对称；选"已删除"即查回收站 -->
          <el-form-item label="回收站">
            <el-select v-model="queryForm.include_deleted" placeholder="正常" clearable>
              <el-option label="正常" value="" />
              <el-option label="已删除" value="deleted" />
            </el-select>
          </el-form-item>
          <el-form-item label="安装日期">
            <el-date-picker
              v-model="queryForm.install_date"
              type="date"
              value-format="yyyy-MM-dd"
              placeholder="选择安装日期"
              clearable
            />
          </el-form-item>
          <!-- 价格区间：挪到左列"固定资产"正下方（grid 第 4 行第 1 列） -->
          <el-form-item label="价格区间" class="price-range">
            <el-input
              v-model="queryForm.price_min"
              type="number"
              placeholder="最低价"
              clearable
            />
            <span class="range-separator">-</span>
            <el-input
              v-model="queryForm.price_max"
              type="number"
              placeholder="最高价"
              clearable
            />
          </el-form-item>
          <!-- 占位：第 4 行中列留空，使到期日期保持在右列 -->
          <div class="grid-placeholder"></div>
          <el-form-item label="到期日期" class="expire-range">
            <el-date-picker
              v-model="queryForm.warranty_expiry_date_start"
              type="date"
              value-format="yyyy-MM-dd"
              placeholder="最早到期"
              clearable
            />
            <span class="range-separator">-</span>
            <el-date-picker
              v-model="queryForm.warranty_expiry_date_end"
              type="date"
              value-format="yyyy-MM-dd"
              placeholder="最晚到期"
              clearable
            />
          </el-form-item>
          <el-form-item class="search-buttons">
            <el-button type="primary" icon="el-icon-search" size="small" @click="handleQuery">查询</el-button>
            <el-button icon="el-icon-refresh-right" size="small" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </el-collapse-transition>
    </el-card>

    <!-- 操作按钮 -->
    <div class="toolbar">
      <el-button type="primary" icon="el-icon-plus" @click="handleAdd">新增</el-button>
      <el-button icon="el-icon-download" @click="handleExport">导出选中</el-button>
      <el-button icon="el-icon-folder" @click="handleExportFiles">导出资质文件</el-button>
      <!-- 标签导出拆成两项：图片（解压即打）/ 数据（配标签软件原生二维码模板，更清晰） -->
      <el-dropdown split-button class="label-export" @click="handleExportLabels" @command="handleLabelCommand">
        <i class="el-icon-printer"></i> 导出二维码标签
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="images">标签图片（ZIP，解压即打）</el-dropdown-item>
          <el-dropdown-item command="data">标签数据（Excel，配标签软件模板）</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <!-- 台账表格 -->
    <el-table ref="mainTable" :data="tableData" row-key="id" border v-loading="loading" class="equipment-table" @selection-change="handleSelectionChange" @sort-change="handleSortChange">
      <el-table-column type="selection" width="55" :reserve-selection="true" />
      <el-table-column type="index" :index="mainIndex" label="序号" width="60" />
      <!-- 点击表头按科室排序：升序/降序/取消（取消后回到默认「按添加时间倒序」） -->
      <el-table-column prop="department" label="科室" width="120" sortable="custom" />
      <el-table-column label="设备名称" min-width="140">
        <template slot-scope="scope">
          <el-link type="primary" @click="goDetail(scope.row)">{{ scope.row.name }}</el-link>
        </template>
      </el-table-column>
      <el-table-column prop="model" label="型号" width="120" />
      <el-table-column prop="factory_no" label="出厂编号" width="140" />
      <el-table-column prop="manufacturer" label="生产厂家" min-width="160" show-overflow-tooltip />
      <el-table-column label="价格" width="100">
        <template slot-scope="scope">
          <span v-if="scope.row.price !== null && scope.row.price !== undefined">¥{{ scope.row.price }}</span>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="production_date" label="生产日期" width="110" />
      <el-table-column prop="service_life" label="使用期限" width="90" />
      <el-table-column prop="warranty_expiry_date" label="到期日期" width="110" />
      <el-table-column prop="install_date" label="安装日期" width="110" />
      <el-table-column prop="status_display" label="使用状态" width="110">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.is_deleted" type="info" size="mini">已删除</el-tag>
          <span v-else>{{ scope.row.status_display }}</span>
        </template>
      </el-table-column>
      <el-table-column label="固定资产" width="90">
        <template slot-scope="scope">
          <span>{{ scope.row.is_fixed_asset ? '是' : '否' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip />
      <el-table-column label="操作" width="150" fixed="right">
        <template slot-scope="scope">
          <el-link type="primary" @click="handleEdit(scope.row)">修改</el-link>
          <!-- 已删除设备不再显示删除入口（后端批量/单条删除接口已排除 is_deleted 记录） -->
          <el-link
            v-if="!scope.row.is_deleted"
            type="danger"
            style="margin-left: 10px"
            @click="handleDelete(scope.row)"
          >删除</el-link>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      class="pagination"
      background
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      :page-size="pageSize"
      :current-page="page"
      :page-sizes="[10, 20, 50]"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
    />

    <!-- 新增弹窗 -->
    <EquipmentForm :visible.sync="formVisible" @success="handleSearch" />

    <!-- 导出资质文件：选择导出内容 -->
    <el-dialog title="选择导出内容" :visible="exportDialogVisible" @update:visible="exportDialogVisible = $event" width="440px">
      <div class="export-tip">已勾选 <b>{{ selectedRows.length }}</b> 台设备，请选择要导出的文件类型：</div>
      <el-checkbox-group v-model="exportTypes">
        <el-checkbox label="qualification">资质文件（PDF）</el-checkbox>
        <el-checkbox label="qrcode">二维码</el-checkbox>
        <el-checkbox label="photo">照片（设备照片 + 铭牌）</el-checkbox>
      </el-checkbox-group>
      <span slot="footer">
        <el-button @click="exportDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmExport" :loading="exporting">确定导出</el-button>
      </span>
    </el-dialog>

    <!-- 编辑/查看弹窗：展示设备信息 + 关联资质 -->
    <EquipmentEditDialog :visible.sync="editVisible" :equipment-id="currentRow ? currentRow.id : null" @success="handleSearch" />

    <!-- 设备详情弹窗 -->
    <el-dialog
      title="设备详情"
      :visible="detailVisible"
      @update:visible="detailVisible = $event"
      width="880px"
      :close-on-click-modal="false"
      top="5vh"
    >
      <equipment-detail v-if="detailVisible" :id="detailId" @close="detailVisible = false" />
    </el-dialog>
  </div>
</template>

<script>
import EquipmentForm from './EquipmentForm.vue'
import EquipmentEditDialog from './EquipmentEditDialog.vue'
import EquipmentDetail from './EquipmentDetail.vue'
import DepartmentSelect from './DepartmentSelect.vue'
import equipmentApi from '../api/equipment'
import * as XLSX from 'xlsx'

export default {
  name: 'EquipmentTable',
  components: { EquipmentForm, EquipmentEditDialog, EquipmentDetail, DepartmentSelect },
  data() {
    return {
      queryForm: {
        department: '',
        name: '',
        factory_no: '',
        manufacturer: '',
        model: '',
        price_min: '',
        price_max: '',
        install_date: '',
        warranty_expiry_date_start: '',
        warranty_expiry_date_end: '',
        status: '',
        include_deleted: '',
        is_fixed_asset: '',
      },
      searchExpanded: false,
      tableData: [],
      total: 0,
      page: 1,
      pageSize: 10,
      ordering: '',   // 后端排序字段（如 'department' / '-department'），空=默认按添加时间倒序
      loading: false,
      backendError: false,
      formVisible: false,
      editVisible: false,
      detailVisible: false,
      detailId: null,
      currentRow: null,
      selectedRows: [],
      exportDialogVisible: false,
      exportTypes: ['qualification'],
      exporting: false,
    }
  },
  mounted() {
    this.handleSearch()
  },
  methods: {
    // 点「查询」/筛选条件变化：必须回到第 1 页。
    // 否则在第 3 页改条件、结果只有 1 页时会请求越界页 → 后端 404 → "查询失败"。
    handleQuery() {
      this.page = 1
      this.handleSearch()
    },
    // 查询参数统一构造：回收站下拉（''=正常 / 'deleted'=已删除）转成后端 is_deleted=true。
    // 查询、导出序号映射都走这里，避免两处口径不一致导致导出序号错位。
    buildQueryParams() {
      const q = Object.assign({}, this.queryForm)
      const recycle = q.include_deleted
      delete q.include_deleted
      if (recycle === 'deleted') q.is_deleted = 'true'
      return q
    },
    async handleSearch(retry = false) {
      this.loading = true
      this.backendError = false
      try {
        const res = await equipmentApi.list({
          ...this.buildQueryParams(),
          page: this.page,
          page_size: this.pageSize,
          ordering: this.ordering,
        })
        this.tableData = res.data.results
        this.total = res.data.count
      } catch (e) {
        const status = e.response && e.response.status
        // 兜底：旧分页器对越界页返回 404（Invalid page），回第 1 页重试一次，
        // 避免前后端版本灰度不一致时用户在深页码上看到"查询失败"。
        if (!retry && status === 404 && this.page > 1) {
          this.page = 1
          this.handleSearch(true)
          return
        }
        const isBackendDown = !e.response || (status && status >= 500)
        if (isBackendDown) {
          // 后端未启动或代理 500，全局拦截器已提示，这里只标记状态显示黄色条
          this.backendError = true
          this.tableData = []
          this.total = 0
        } else {
          this.$message.error('查询失败')
        }
      } finally {
        this.loading = false
      }
    },
    handleReset() {
      this.queryForm = {
        department: '',
        name: '',
        factory_no: '',
        manufacturer: '',
        model: '',
        price_min: '',
        price_max: '',
        install_date: '',
        warranty_expiry_date_start: '',
        warranty_expiry_date_end: '',
        status: '',
        include_deleted: '',
        is_fixed_asset: '',
      }
      this.page = 1
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.handleSearch()
    },
    handlePageChange(val) {
      this.page = val
      this.handleSearch()
    },
    // 列头排序：三态循环 升序 → 降序 → 取消（取消后恢复默认按添加时间倒序）
    handleSortChange({ prop, order }) {
      if (!order) {
        this.ordering = ''
      } else {
        this.ordering = (order === 'descending' ? '-' : '') + prop
      }
      // 保持当前页码：排序由后端在全量数据上做，用户在第几页排序就显示排序后的第几页。
      // 排序后若当前页越界（结果变少），后端宽容分页会回退到最后一页，不会报错。
      this.handleSearch()
    },
    handleAdd() {
      this.currentRow = null
      this.formVisible = true
    },
    handleEdit(row) {
      this.currentRow = row
      this.editVisible = true
    },
    handleDelete(row) {
      this.$confirm(`确定删除设备 "${row.name}" 吗？此操作不可恢复。`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(async () => {
          try {
            await equipmentApi.delete(row.id)
            this.$message.success('删除成功')
            // 跨页保留勾选模式下，清空整个勾选（避免已删行残留在勾选中）
            this.$refs.mainTable.clearSelection()
            this.handleSearch()
          } catch (e) {
            this.$message.error('删除失败')
          }
        })
        .catch(() => {})
    },
    goDetail(row) {
      this.detailId = row.id
      this.detailVisible = true
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows
    },
    // 导出资质文件：先校验勾选，再弹窗选择导出内容
    handleExportFiles() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先在表格中勾选要导出的设备')
        return
      }
      this.exportTypes = ['qualification']
      this.exportDialogVisible = true
    },
    // 标签导出的下拉分发：图片走 ZIP，数据走 Excel
    handleLabelCommand(cmd) {
      if (cmd === 'data') {
        this.handleExportLabelData()
      } else {
        this.handleExportLabels()
      }
    },
    // 导出标签打印数据（Excel）：给标签打印机自带的设计软件做变量批量打印用。
    // 那条路二维码由打印机的原生控件渲染，比打印位图清晰得多，也不会被拉伸变形。
    async handleExportLabelData() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先在表格中勾选要导出的设备')
        return
      }
      const ids = this.selectedRows.map((r) => r.id)
      try {
        const res = await equipmentApi.exportLabelData({ ids })
        this.saveBlob(res.data, `标签数据_${this.getNowStamp()}.xlsx`)
        this.$message.success('已导出标签数据，可在标签软件里按列映射字段')
      } catch (e) {
        let msg = '导出失败，请稍后重试'
        try {
          if (e.response && e.response.data) {
            const text = await e.response.data.text()
            const obj = JSON.parse(text)
            if (obj.detail) msg = obj.detail
          }
        } catch (_) {}
        this.$message.error(msg)
      }
    },
    // 导出二维码标签：后端已把「二维码 + 科室 / 型号 / 设备编号 / 导出时间」合成成一张图，
    // 解压后直接打印或交给标签打印机，不用再在标签软件里排版。
    async handleExportLabels() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先在表格中勾选要导出的设备')
        return
      }
      const ids = this.selectedRows.map((r) => r.id)
      const loading = this.$loading({ lock: true, text: '正在生成标签图…' })
      try {
        const res = await equipmentApi.exportQrcodeLabels({ ids })
        this.saveBlob(res.data, `设备二维码标签_${this.getNowStamp()}.zip`)
        this.$message.success(`已生成 ${ids.length} 张标签，解压后可直接打印`)
      } catch (e) {
        // 后端 4xx/5xx 的 data 是 blob 包着的 JSON，解析出真实 detail
        let msg = '导出失败，请稍后重试'
        try {
          if (e.response && e.response.data) {
            const text = await e.response.data.text()
            const obj = JSON.parse(text)
            if (obj.detail) msg = obj.detail
          }
        } catch (_) {}
        this.$message.error(msg)
      } finally {
        loading.close()
      }
    },
    // 弹窗确认：调用后端打包 ZIP 并触发下载
    async confirmExport() {
      if (!this.exportTypes.length) {
        this.$message.warning('请至少选择一种导出内容')
        return
      }
      this.exporting = true
      const ids = this.selectedRows.map((r) => r.id)
      try {
        const res = await equipmentApi.exportFiles({ ids, types: this.exportTypes })
        const ct = (res.headers && res.headers['content-type']) || ''
        if (ct.indexOf('application/zip') === -1) {
          // 兜底：非 zip（理论不会到这，后端 4xx 会走 catch）
          const text = await res.data.text()
          const msg = (JSON.parse(text).detail) || '导出失败'
          this.$message.error(msg)
          return
        }
        this.saveBlob(res.data, `设备资质导出_${this.getNowStamp()}.zip`)
        this.$message.success('导出成功，已开始下载')
        this.exportDialogVisible = false
      } catch (e) {
        // 后端返回 4xx 时 data 为 JSON（blob 形式），尝试解析提示
        let msg = '导出失败，请稍后重试'
        try {
          if (e.response && e.response.data) {
            const text = await e.response.data.text()
            const obj = JSON.parse(text)
            if (obj.detail) msg = obj.detail
          }
        } catch (_) {}
        this.$message.error(msg)
      } finally {
        this.exporting = false
      }
    },
    // 导出：仅导出当前勾选的记录，表头与页面表格“备注”列及之前完全一致
    // 主列表序号：跨页连续编号（第 2 页从 11 开始，不重新起 1）
    mainIndex(index) {
      return (this.page - 1) * this.pageSize + index + 1
    },
    // 拉取与当前筛选一致的全量列表，建立 id→序号 映射（序号=在全量结果中的位置，与页面显示一致）
    // 后端 max_page_size=100，超过 100 条时循环翻页补齐
    async fetchSeqMap() {
      const map = {}
      try {
        const pageSize = 100
        let page = 1
        let total = Infinity
        while ((page - 1) * pageSize < total) {
          // 带上当前排序，保证导出序号与页面显示顺序一致
          const res = await equipmentApi.list({ ...this.buildQueryParams(), page, page_size: pageSize, ordering: this.ordering })
          const rows = res.data.results || res.data
          rows.forEach((row, i) => { map[row.id] = (page - 1) * pageSize + i + 1 })
          total = res.data.count || rows.length
          if (!rows.length) break
          page++
        }
      } catch (e) {
        // 拉取失败返回空映射，序号留空但不阻塞导出
      }
      return map
    },
    async handleExport() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先在表格中勾选要导出的设备')
        return
      }
      const seqMap = await this.fetchSeqMap()
      // 列定义与表格展示顺序一致，截止到“备注”列（不含操作/资质/照片/二维码）
      const columns = [
        { label: '序号', key: '__index' },
        { label: '科室', key: 'department' },
        { label: '设备名称', key: 'name' },
        { label: '型号', key: 'model' },
        { label: '出厂编号', key: 'factory_no' },
        { label: '生产厂家', key: 'manufacturer' },
        { label: '价格', key: 'price' },
        { label: '生产日期', key: 'production_date' },
        { label: '使用期限', key: 'service_life' },
        { label: '到期日期', key: 'warranty_expiry_date' },
        { label: '安装日期', key: 'install_date' },
        { label: '使用状态', key: 'status_display' },
        { label: '固定资产', key: 'is_fixed_asset' },
        { label: '备注', key: 'remark' },
      ]
      // 只处理选中项，按勾选顺序编号
      const data = this.selectedRows.map((row, i) => {
        const item = {}
        columns.forEach((col) => {
          if (col.key === '__index') {
            item[col.label] = seqMap[row.id] || ''
          } else if (col.key === 'is_fixed_asset') {
            // 布尔字段导出为“是/否”
            item[col.label] = row[col.key] ? '是' : '否'
          } else {
            const val = row[col.key]
            item[col.label] = (val === null || val === undefined) ? '' : val
          }
        })
        return item
      })
      const ws = XLSX.utils.json_to_sheet(data, { header: columns.map((c) => c.label) })
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '设备台账')
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      this.saveBlob(wbout, `设备台账_${this.getNowStamp()}.xlsx`)
      this.$message.success(`已成功导出 ${this.selectedRows.length} 条记录`)
    },
    // 通用：将二进制流（或已生成的 Blob）下载为文件
    saveBlob(buffer, fileName) {
      const blob = buffer instanceof Blob
        ? buffer
        : new Blob([buffer], { type: 'application/octet-stream' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    // 生成文件名时间戳 YYYYMMDD_HHMMSS
    getNowStamp() {
      const d = new Date()
      const p = (n) => (n < 10 ? '0' + n : '' + n)
      return (
        '' + d.getFullYear() + p(d.getMonth() + 1) + p(d.getDate()) +
        '_' + p(d.getHours()) + p(d.getMinutes()) + p(d.getSeconds())
      )
    },
  },
}
</script>

<style scoped>
.search-card {
  margin-bottom: 15px;
}
.search-card /deep/ .el-card__body {
  padding: 0;
}
.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.search-title {
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  user-select: none;
}
.search-title:hover {
  color: #409eff;
}
.search-form {
  padding: 15px 15px 0 15px;
}
/* 查询区整体缩小一号：label 13px、输入框 13px/高 32px、间距收紧 */
.search-form /deep/ .el-form-item__label {
  font-weight: bold;
  font-size: 13px;
}
.search-form /deep/ .el-input__inner {
  border-color: #409eff;
  font-size: 13px;
  height: 32px;
  line-height: 32px;
  color: #303133;
}
.search-form /deep/ .el-input__inner::placeholder {
  font-size: 13px;
}
.search-form /deep/ .el-input__icon {
  line-height: 32px;
}
.search-form /deep/ .el-input__inner:focus {
  border-color: #409eff;
}
.search-form /deep/ .el-input__prefix {
  color: #409eff;
}
/* 查询区：整齐的 3 列网格布局 */
.search-form {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 16px;
  row-gap: 10px;
  padding: 10px;
  align-items: start;
}
.search-form /deep/ .el-form-item {
  margin: 0;
  width: 100%;
}
.search-form /deep/ .el-input,
.search-form /deep/ .el-select,
.search-form /deep/ .el-date-editor {
  width: 100%;
}
/* 价格区间：占 1 格，内部两个输入框并排 */
.search-form .el-form-item.price-range {
  display: flex;
  align-items: center;
}
.search-form .price-range /deep/ .el-form-item__label {
  line-height: 32px;
  padding-right: 8px;
  white-space: nowrap;
}
.search-form .price-range /deep/ .el-form-item__content {
  flex: 1;
  display: flex;
  align-items: center;
  margin-left: 0 !important;
}
.search-form .price-range /deep/ .el-input {
  width: 100%;
  flex: 1;
}
/* 到期日期区间：占 1 格，内部两个日期并排 */
.search-form .el-form-item.expire-range {
  grid-column: span 1;
  display: flex;
  align-items: center;
  margin: 0;
}
.search-form .expire-range /deep/ .el-form-item__label {
  line-height: 32px;
  padding-right: 8px;
  white-space: nowrap;
}
.search-form .expire-range /deep/ .el-form-item__content {
  flex: 1;
  display: flex;
  align-items: center;
  margin-left: 0 !important;
}
.search-form .expire-range /deep/ .el-date-editor {
  flex: 1;
}
/* 查询 / 重置按钮：固定在第 3 列（最右侧），自然落到最后一行右下角 */
.search-form .el-form-item.search-buttons {
  grid-column: 3;
  margin: 0;
}
.search-form .search-buttons /deep/ .el-form-item__content {
  display: flex;
  justify-content: flex-end;
}
/* 占位：让第 3 行右侧空出，把价格区间/到期日期/按钮压到第 4 行 */
.grid-placeholder {
  visibility: hidden;
}
.range-separator {
  margin: 0 8px;
  color: #909399;
  flex-shrink: 0;
}
.toolbar {
  margin-bottom: 15px;
}
/* el-dropdown 是 div 包裹，拿不到 .el-button + .el-button 的默认左间距，手动补上 */
.toolbar .label-export {
  margin-left: 10px;
  vertical-align: top;
}
.pagination {
  margin-top: 15px;
  text-align: right;
}
.equipment-table /deep/ .el-table__body tr:hover > td {
  background-color: #ecf5ff !important;
}
/* 台账表格：表头和单元格内容全部居中 */
.equipment-table /deep/ .el-table__header th,
.equipment-table /deep/ .el-table__body td {
  text-align: center !important;
}
</style>
